import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/carePlan/CarePlan.module.css";
import ClientAvatar from "@components/ClientAvatar";
import Container from "@components/Container";
import SupportTeamForm from "@components/forms/SupportTeamForm";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import timezones from "@i18n/timezones";
import {
  ClientRoleDestroyDocument,
  ClientRoleDestroyMutation,
  ClientRoleDestroyMutationVariables,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { getCareProfileName } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";

type Journey = GetJourneyWithPlanQuery["journey"];

type RoleDescriptionProps = {
  journey: Journey;
  role: Journey["clientRoles"][number];
};

const RoleDescription = ({ journey, role }: RoleDescriptionProps) => {
  const intl = useIntl();

  return (
    <div className="flex flex-column gap-8">
      {role.relationship && (
        <div className={`flex items-center ${styles.careTeamDetail}`}>
          <Icon className="ion-margin-end" color="gray-200" icon="user" />
          <BodyCopy>
            {role.relationship.relationshipOption ? (
              <FormattedMessage
                id={`models.clientRole.relationship.${role.relationship.relationshipOption}`}
                values={{ name: getCareProfileName({ intl, journey }) }}
              />
            ) : (
              role.relationship.other
            )}
          </BodyCopy>
        </div>
      )}
      {role.client.consultPhoneNumber?.formattedNumber && (
        <div className={`flex ${styles.careTeamDetail}`}>
          <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="phone" />
          <BodyCopy>{role.client.consultPhoneNumber.formattedNumber}</BodyCopy>
        </div>
      )}
      {role.client.email.email && (
        <div className={`flex ${styles.careTeamDetail}`}>
          <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="envelope" />
          <BodyCopy>{role.client.email.email}</BodyCopy>
        </div>
      )}
      {role.client.timezone && (
        <div className={`flex ${styles.careTeamDetail}`}>
          <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="globe" />
          <BodyCopy>{timezones[role.client.timezone]}</BodyCopy>
        </div>
      )}
      {role.client.location.address && (
        <div className={`flex ${styles.careTeamDetail}`}>
          <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="locationDot" />
          <BodyCopy>{role.client.location.address}</BodyCopy>
        </div>
      )}
    </div>
  );
};

type Props = {
  canEdit: boolean;
  canRemove: boolean;
  color: Colors;
  journey: Journey;
  role: Journey["clientRoles"][number];
};

const CareTeamMember = ({ canEdit, canRemove, color, journey, role }: Props) => {
  const opener = useOpener();
  const modalOpener = useOpener();
  const editingOpener = useOpener();
  const intl = useIntl();

  const [destroyClientRole, apolloClient] = useMutationWithValidation<
    ClientRoleDestroyMutation,
    ClientRoleDestroyMutationVariables
  >(ClientRoleDestroyDocument, "clientRoleDestroy", {
    update: () => {
      apolloClient.resetStore();
    }
  });

  const relationshipDescription = useMemo(() => {
    if (role.relationship?.other) {
      return role.relationship.other;
    }

    if (role.relationship?.relationshipOption) {
      return intl.formatMessage(
        {
          id: `models.clientRole.relationshipInverse.${role.relationship.relationshipOption}`
        },
        { name: getCareProfileName({ intl, journey }) }
      );
    }
  }, [intl, journey, role.relationship?.other, role.relationship?.relationshipOption]);

  return (
    <div className="margin-20-bottom">
      <Container className={styles.careTeamContainer}>
        <IonGrid className="ion-no-padding">
          <IonRow className="ion-align-items-center pointer" onClick={isMobile() ? modalOpener.toggle : opener.toggle}>
            <IonCol size="auto">
              <div className="ion-margin-end">
                <ClientAvatar
                  bold
                  clientId={role.client.id}
                  color={color}
                  firstName={role.client.firstName}
                  journeyId={journey.id}
                  size="large"
                />
              </div>
            </IonCol>
            <IonCol>
              <Heading2>{role.client.fullName}</Heading2>
              <div className="margin-4-top">
                <BodyCopy bold color="gray" size="small">
                  {relationshipDescription}
                </BodyCopy>
              </div>
            </IonCol>
            <IonCol className="ion-hide-lg-down" size="auto">
              {(canEdit || canRemove) && (
                <div className="flex gap-md">
                  {canEdit && (
                    <CircleButton
                      ariaLabelKey="pages.client.careProfile.careTeam.editDetails"
                      color="gray-300"
                      fill="clear"
                      icon="edit"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();

                        opener.open();
                        editingOpener.toggle();
                      }}
                    />
                  )}
                  {canRemove && (
                    <CircleButton
                      ariaLabelKey="pages.client.careProfile.careTeam.remove"
                      color="gray-300"
                      icon="trash"
                      onClick={() =>
                        destroyClientRole({
                          id: role.id
                        })
                      }
                    />
                  )}
                </div>
              )}
            </IonCol>
          </IonRow>
          <ExpandableSection className="default-cursor" initial={false} isOpen={opener.isOpen}>
            <div className={`ion-margin-top ${styles.supportTeamFormWidth}`}>
              {editingOpener.isOpen ? (
                <SupportTeamForm
                  autofocus
                  careProfileName={journey.careProfile.name}
                  client={role.client}
                  clientRole={role}
                  onCancel={editingOpener.close}
                  onError={() => {}}
                  onSuccess={editingOpener.close}
                />
              ) : (
                <RoleDescription journey={journey} role={role} />
              )}
            </div>
          </ExpandableSection>
        </IonGrid>
      </Container>
      <Modal opener={modalOpener} title={role.client.fullName}>
        {() => (
          <div>
            <div>
              {editingOpener.isOpen ? (
                <div className="margin-10-left margin-10-right">
                  <SupportTeamForm
                    autofocus={false}
                    careProfileName={journey.careProfile.name}
                    client={role.client}
                    clientRole={role}
                    onCancel={editingOpener.close}
                    onError={() => {}}
                    onSuccess={editingOpener.close}
                  />
                </div>
              ) : (
                <div>
                  <RoleDescription journey={journey} role={role} />
                  <div className={`${styles.modalActions} flex flex-column gap-8`}>
                    {canRemove && (
                      <ButtonLink
                        className={`flex ${styles.careTeamDetail}`}
                        onClick={() =>
                          destroyClientRole({
                            id: role.id
                          })
                        }
                        stylized={false}
                      >
                        <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="trash" />
                        <BodyCopy>
                          <FormattedMessage id="dictionary.remove" />
                        </BodyCopy>
                      </ButtonLink>
                    )}
                    {canEdit && (
                      <ButtonLink
                        className={`flex ${styles.careTeamDetail}`}
                        onClick={() => {
                          opener.close();
                          editingOpener.toggle();
                        }}
                        stylized={false}
                      >
                        <Icon className="ion-margin-end margin-2-top" color="gray-200" icon="edit" />
                        <BodyCopy>
                          <FormattedMessage id="dictionary.edit" />
                        </BodyCopy>
                      </ButtonLink>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CareTeamMember;
